import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { color, mono } from '../tokens';
import {
  type Insight, type InsightKind, type Opportunity, type Sizing,
} from '../fixtures';
import type { SynthesisPayload } from '../types';
import { useSynth, useSynthProjectId, useSynthApply } from '../SynthesisContext';
import { asText, cleanLabel } from '../asText';
import { apiClient } from '../../../api';
import { countOf } from '../../../utils/pluralize';

const KIND_STYLE: Record<InsightKind, { bg: string; fg: string; border: string; blurb: string }> = {
  'PATTERN':       { bg: '#F5F3FF', fg: '#6D28D9', border: '#DDD6FE', blurb: 'Same observation recurs — signal is frequency.' },
  'CONTRADICTION': { bg: '#FEE2E2', fg: '#DC2626', border: '#FCA5A5', blurb: 'Sources disagree about the same workflow.' },
  'CONVERGENCE':   { bg: '#DCFCE7', fg: '#15803D', border: '#86EFAC', blurb: 'Different observations, same root cause.' },
  'LONG-TAIL':     { bg: '#F1F5F9', fg: '#475569', border: '#CBD5E1', blurb: 'Single-source signal worth watching.' },
};

const SIZE_STYLE: Record<Sizing, { bg: string; fg: string }> = {
  H: { bg: '#FEE2E2', fg: '#DC2626' },
  M: { bg: '#FEF3C7', fg: '#B45309' },
  L: { bg: '#DCFCE7', fg: '#15803D' },
};

function ConfidenceBar({ v }: { v: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: 90, height: 6, borderRadius: 999, background: '#F1F5F9', overflow: 'hidden' }}>
        <div style={{ width: `${Math.round(v * 100)}%`, height: '100%', background: color.purpleGrad }} />
      </div>
      <span style={{ fontSize: 11, fontFamily: mono, color: color.textMuted }}>{Math.round(v * 100)}%</span>
    </div>
  );
}

function InsightCard({ ins }: { ins: Insight }) {
  // Defensive: an unexpected kind falls back to the PATTERN style, never a crash.
  const k = KIND_STYLE[ins.kind] || KIND_STYLE.PATTERN;
  const evidenceRows = (ins.evidence || []).filter((e) => asText(e.finding).trim().length > 0);
  return (
    <div style={{
      background: color.surface, border: `1px solid ${color.hairline}`,
      borderLeft: `3px solid ${k.fg}`, borderRadius: 10, padding: '16px 18px', marginBottom: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', padding: '2px 8px', borderRadius: 6,
          background: k.bg, color: k.fg, border: `1px solid ${k.border}`,
        }}>{asText(ins.kind)}</span>
        <span style={{ fontSize: 15, fontWeight: 600, color: color.ink }}>{asText(ins.title)}</span>
        <span style={{ marginLeft: 'auto' }}><ConfidenceBar v={ins.confidence} /></span>
      </div>

      <p style={{ margin: '10px 0 0', fontSize: 13, color: color.text, lineHeight: 1.55 }}>{asText(ins.body)}</p>

      <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint, marginBottom: 4 }}>WHY IT MATTERS</div>
          <p style={{ margin: 0, fontSize: 12.5, color: color.textMuted, lineHeight: 1.5 }}>{asText(ins.implication)}</p>
        </div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint, marginBottom: 4 }}>RATIONALE</div>
          <p style={{ margin: 0, fontSize: 12.5, color: color.textMuted, lineHeight: 1.5 }}>{asText(ins.rationale)}</p>
        </div>
      </div>

      {evidenceRows.length > 0 && (
        <div style={{ marginTop: 12, borderTop: `1px solid ${color.hairline}`, paddingTop: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint, marginBottom: 6 }}>
            EVIDENCE · {countOf(ins.coverage, 'interview')}
          </div>
          {evidenceRows.map((e, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 4 }}>
              <span style={{ fontSize: 12.5, color: color.text, flex: 1 }}>{asText(e.finding)}</span>
              <span style={{ fontSize: 11, fontFamily: mono, color: color.textMuted }}>{e.mentions}× · {asText(e.sources)}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {ins.themes.map((t) => (
          <span key={t} style={{
            fontSize: 11, fontFamily: mono, padding: '2px 7px', borderRadius: 6,
            background: color.pillActiveBg, color: color.purpleDeep,
          }}>Theme {String(t).padStart(2, '0')}</span>
        ))}
      </div>
    </div>
  );
}

function SizeChip({ label, size }: { label: string; size: Sizing }) {
  // Defensive: any size outside L/M/H (unexpected data) falls back to the M style, never a crash.
  const s = SIZE_STYLE[size] || SIZE_STYLE.M;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: color.textMuted }}>
      {label}
      <span style={{
        fontSize: 11, fontWeight: 700, fontFamily: mono, padding: '1px 7px', borderRadius: 6,
        background: s.bg, color: s.fg,
      }}>{asText(size)}</span>
    </span>
  );
}

const FINDING_KEY_RE = /^[0-9a-f-]{8,}-[0-9a-f-]+#\d+$/i;

function OpportunityCard({ op, themeNameByRank }: { op: Opportunity; themeNameByRank: Map<number, string> }) {
  const quickWin = op.impact === 'H' && op.effort === 'L';
  const fallbackThemeNames = (op.themes || [])
    .map((rank) => asText(themeNameByRank.get(rank)))
    .filter((name) => name && !FINDING_KEY_RE.test(name));
  const supportNames = (op.supports || [])
    .map((s, i) => {
      const txt = asText(s).trim();
      if (!txt) return '';
      if (FINDING_KEY_RE.test(txt)) return asText(fallbackThemeNames[i] || fallbackThemeNames[0] || '');
      return txt;
    })
    .filter((s) => s && !FINDING_KEY_RE.test(s));
  const dedupedSupports = Array.from(new Set(supportNames.length > 0 ? supportNames : fallbackThemeNames));
  return (
    <div style={{
      background: color.surface, border: `1px solid ${quickWin ? '#86EFAC' : color.hairline}`,
      borderRadius: 10, padding: '16px 18px', marginBottom: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: color.ink }}>{asText(op.title)}</span>
        {quickWin && (
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', padding: '2px 8px', borderRadius: 6,
            background: '#DCFCE7', color: '#15803D', border: '1px solid #86EFAC',
          }}>QUICK WIN</span>
        )}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 14 }}>
          <SizeChip label="impact" size={op.impact} />
          <SizeChip label="effort" size={op.effort} />
        </span>
      </div>

      <p style={{ margin: '10px 0 0', fontSize: 13, color: color.text, lineHeight: 1.55 }}>{asText(op.desc)}</p>

      <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
        {[['METRIC', op.metric], ['COST', op.cost], ['RISKS', op.risks]].map(([label, val]) => (
          <div key={label}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint, marginBottom: 4 }}>{label}</div>
            <p style={{ margin: 0, fontSize: 12, color: color.textMuted, lineHeight: 1.5 }}>{asText(val)}</p>
          </div>
        ))}
      </div>

      {dedupedSupports.length > 0 && (
        <div style={{ marginTop: 12, borderTop: `1px solid ${color.hairline}`, paddingTop: 10 }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint }}>MOVES </span>
          {dedupedSupports.map((s, i) => (
            <span key={i} style={{ fontSize: 12, color: color.text }}>
              {asText(s)}{i < dedupedSupports.length - 1 ? ' · ' : ''}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Insights() {
  const { insights, opportunities, themes } = useSynth();
  const projectId = useSynthProjectId();
  const applyPayload = useSynthApply();
  const kinds: InsightKind[] = ['PATTERN', 'CONTRADICTION', 'CONVERGENCE', 'LONG-TAIL'];

  // Regenerate Insights & Opportunities TOGETHER (Ryan Point 4). One server call re-authors both
  // from the same synthesized base and swaps the payload in place — the theme numbers are untouched.
  const [regenOpen, setRegenOpen] = useState(false);
  const [instruction, setInstruction] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  const [regenError, setRegenError] = useState<string | null>(null);
  const canRegen = projectId != null && !!applyPayload;
  const themeNameByRank = new Map(themes.map((t) => [t.rank, cleanLabel(t.name)]));

  async function regenerate() {
    if (!canRegen || regenerating) return;
    setRegenerating(true);
    setRegenError(null);
    const pid = encodeURIComponent(String(projectId));
    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
    const done = (payload: any) => {
      if (payload && Array.isArray(payload.themes)) { applyPayload!(payload as SynthesisPayload); setRegenOpen(false); setInstruction(''); }
      else setRegenError('Regenerate finished but returned no result.');
    };
    try {
      const res = await apiClient.post(`/api/synthesis-viz/${pid}/regenerate-insights`, { instruction });
      const data = res?.data || {};
      // Sync fallback (no queue): the payload comes straight back.
      if (data.state === 'completed' && data.payload) { done(data.payload); return; }
      const jobId = data.jobId;
      if (!jobId) { setRegenError('Regenerate did not start.'); return; }
      // Poll the background job until it finishes (the Claude call is multi-second).
      for (let i = 0; i < 120; i++) {
        await sleep(2500);
        let st;
        try { st = await apiClient.get(`/api/synthesis-viz/${pid}/run/${encodeURIComponent(String(jobId))}`); }
        catch { continue; }
        const s = st?.data || {};
        if (s.state === 'completed') { done(s.payload); return; }
        if (s.state === 'failed') { setRegenError(s.error || 'Regenerate failed.'); return; }
        if (s.state === 'unknown') { setRegenError('Regenerate run not found — it may have expired.'); return; }
      }
      setRegenError('Regenerate is taking longer than expected — check back shortly.');
    } catch (err: any) {
      setRegenError(err?.response?.data?.error || (err instanceof Error ? err.message : 'Regenerate failed.'));
    } finally {
      setRegenerating(false);
    }
  }

  return (
    <div>
      {/* Header block */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint }}>TAB</span>
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: color.purple,
          background: color.pillActiveBg, padding: '2px 8px', borderRadius: 6,
        }}>ACTIONS &amp; OPPORTUNITIES</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: color.ink, letterSpacing: '-0.01em' }}>
            Actions & Opportunities
          </h2>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: color.textMuted }}>
            Patterns, contradictions and convergences across sources — then the bets they justify.
          </p>
        </div>
        {canRegen && (
          <button
            onClick={() => setRegenOpen((v) => !v)}
            disabled={regenerating}
            title="Regenerate Insights & Opportunities together from the current synthesis"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: regenerating ? 'wait' : 'pointer',
              padding: '8px 12px', borderRadius: 8, background: color.surface, color: color.text,
              border: `1px solid ${color.hairline}`, whiteSpace: 'nowrap',
            }}
          >
            <Sparkles size={14} /> {regenerating ? 'Regenerating…' : 'Regenerate'}
          </button>
        )}
      </div>

      {/* Regenerate panel — a lightweight per-tab refine: optional guidance, then redo BOTH sections. */}
      {regenOpen && canRegen && (
        <div style={{
          marginTop: 12, background: color.surfaceAlt, border: `1px solid ${color.hairline}`,
          borderRadius: 10, padding: '12px 14px',
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: color.ink, marginBottom: 8 }}>
            Regenerate Insights &amp; Opportunities <span style={{ color: color.textFaint, fontWeight: 400 }}>· they redo together from the same base</span>
          </div>
          <textarea
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            placeholder="Optional: steer this pass — e.g. 'focus on onboarding friction' or 'be more conservative on effort sizing'."
            rows={2}
            style={{
              width: '100%', boxSizing: 'border-box', fontSize: 13, padding: '8px 10px', borderRadius: 8,
              border: `1px solid ${color.hairline}`, color: color.text, background: color.surface, resize: 'vertical',
            }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
            <button
              onClick={regenerate}
              disabled={regenerating}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: regenerating ? 'wait' : 'pointer',
                padding: '8px 14px', borderRadius: 8, background: color.ink, color: '#fff', border: 'none',
              }}
            >
              <Sparkles size={14} /> {regenerating ? 'Regenerating…' : 'Regenerate both'}
            </button>
            <span style={{ fontSize: 11.5, color: color.textFaint }}>Takes a few seconds — theme numbers stay unchanged.</span>
          </div>
          {regenError && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#B91C1C' }}>⚠ {regenError}</div>
          )}
        </div>
      )}

      <div style={{ height: 1, background: color.hairline, margin: '18px 0' }} />

      {kinds.map((kind) => {
        const group = insights.filter((i) => i.kind === kind);
        if (!group.length) return null;
        return (
          <div key={kind} style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0 10px' }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: KIND_STYLE[kind].fg }}>{kind}</span>
              <span style={{ fontSize: 12, color: color.textFaint }}>{KIND_STYLE[kind].blurb}</span>
              <span style={{ flex: 1, height: 1, background: color.hairline }} />
            </div>
            {group.map((ins, i) => <InsightCard key={i} ins={ins} />)}
          </div>
        );
      })}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '20px 0 12px' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: color.ink }}>OPPORTUNITIES</span>
        <span style={{ fontSize: 12, color: color.textFaint }}>sized by impact × effort — the traceable chain from research to recommendation</span>
        <span style={{ flex: 1, height: 1, background: color.hairline }} />
      </div>
      {opportunities.map((op, i) => <OpportunityCard key={i} op={op} themeNameByRank={themeNameByRank} />)}
    </div>
  );
}
